import { useEffect, useMemo, useState, useCallback, useRef } from "react"
// @ts-expect-error - @rails/actioncable doesn't have TypeScript declarations
import * as ActionCable from "@rails/actioncable"
// @ts-expect-error - currency_helper is a JavaScript file without type declarations
import { CurrencyHelper } from "~/helpers/currency_helper"
import {
  DataTable,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  DataTableCellActions,
  DataTableCheckboxCell,
  DataTableGroupRow,
  DataTableEmptyState,
} from "~/react/ui/data-table"
import { DataTableBulkActionsBar } from "~/react/components/data-table-bulk-actions-bar"
import { ActionsCell } from "~/react/components/data-table/cells/actions-cell"
import { EmailCapacityBreakdown } from "~/react/components/email_accounts_center/email-capacity-breakdown"
import { InboxProtectionModal } from "~/react/components/email_accounts_center/inbox_protection_modal"
import { HealthScoreIndicator } from "~/react/ui/health-score-indicator"
import { Avatar, AvatarFallback } from "~/react/ui/avatar"
import { Tag, TagLabel } from "~/react/ui/tag"
import { DataTableSkeleton } from "~/react/components/data-table/data-table-skeleton"
import {
  campaigns_queue_index_path,
  email_accounts_path,
  email_account_path,
  email_domains_health_checks_path,
  bulk_email_accounts_path,
  bulk_subscription_email_accounts_path,
  subscription_email_account_path,
  capacities_email_accounts_path,
} from "~/lib/routes"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "~/react/ui/tooltip"
import { Skeleton } from "~/react/ui/skeleton"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { Checkbox } from "~/react/ui/checkbox"
import { useTableSelection } from "~/react/hooks/useTableSelection"
import { useEmailAccountUsage } from "~/react/hooks/useEmailAccountUsage"
import {
  FiltersDropdown,
  FiltersTags,
  filterEmailAccounts,
  type EmailAccountFilters,
  type EmailAccount,
  type EmailAccountsData,
  type TeamMember,
} from "~/react/components/email_accounts/email_accounts_filters"
import { getWarmupStateFromProtection } from "~/react/components/email_accounts/utils"
import type {
  BillingInfo,
  CreditCardInfo,
  PricePerWarmup,
} from "~/react/components/email_accounts_center/add_email_account_slots_modal/constants"
import { reconnectEmailAccount, deleteEmailAccount } from "~/react/components/email_accounts/actions"
import { SearchInput } from "~/react/ui/search-input"
import EmailAccountSidepanel from "~/react/components/email_accounts/email_account_sidepanel"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter, SheetBody } from "~/react/ui/sheet"
import EmailAccountProfileTab from "~/react/components/email_accounts/tabs/email_account_profile_tab"
import { Alert, AlertDescription, AlertIcon, AlertContent } from "~/react/ui/alert"
import { getCsrfToken } from "~/lib/utils"
import { email_accounts_mailboxes_path } from "~/lib/routes"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogCloseButton,
  DialogBody,
  DialogFooter,
} from "~/react/ui/dialog"

// Import provider icons to ensure proper asset resolution in production
import gmailIcon from "~/images/connected_apps/gmail_icon.svg"
import outlookIcon from "~/images/connected_apps/outlook_icon.svg"
import smtpImapIcon from "~/images/connected_apps/smtp_imap_icon.svg"

// Map of provider icons using imported assets for proper production builds
const providerIconMap: Record<string, string> = {
  gmail: gmailIcon,
  outlook: outlookIcon,
  smtp_imap: smtpImapIcon,
}

type CapacityData = {
  sent: number
  limit: number
  scheduled: number
}

type Props = {
  total?: number
  teamMembers?: TeamMember[]
  sharedWorkspace?: boolean
  onOpenConnectSidepanel?: (filter?: "managed" | "connected") => void
  onEmailAccountIdChange?: (id: number | null) => void
  initialEmailAccountId?: number | null
  allowEmailProvisioning?: boolean
  isPremium?: boolean
  isWarmupEnabled?: boolean
  stripePublicKey?: string | null
  creditCardInfo?: CreditCardInfo | null
  cardholderName?: string | null
  billingInfo?: BillingInfo | null
  userEmail?: string | null
  pricePerWarmup: PricePerWarmup
  maxEmailAccounts: number
}

type ColumnId = "email" | "sender_name" | "status" | "capacity" | "scheduled" | "health" | "owner" | "actions"

type ColumnDef = {
  id: ColumnId
  sticky?: boolean
  renderHeader: () => React.ReactNode
  renderCell: (account: EmailAccount) => React.ReactNode
  cellProps?: React.TdHTMLAttributes<HTMLTableCellElement>
}

const getInitials = (name: string): string => {
  if (!name) return ""
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase()
}

const STORAGE_KEY = "email_accounts_center_email_accounts.columns"

const getFiltersFromLocation = (): EmailAccountFilters => {
  if (typeof window === "undefined") {
    return {
      status: [],
      healthFrom: null,
      healthTo: null,
      healthIsEmpty: false,
      healthHasAnyValue: false,
      ownerIds: [],
      searchQuery: "",
    }
  }

  const params = new URLSearchParams(window.location.search)

  // Parse status array
  const statusParam = params.get("account_status") || params.get("status")
  const status = statusParam ? statusParam.split("|") : []

  // Parse health range and modes
  const healthHasAnyValue =
    (params.get("account_health_has_any_value") || params.get("health_has_any_value")) === "true"
  const healthFromParam = params.get("account_health_from") || params.get("health_from")
  const parsedHealthFrom = healthFromParam ? parseInt(healthFromParam, 10) : null
  const healthFrom = parsedHealthFrom !== null && Number.isNaN(parsedHealthFrom) ? null : parsedHealthFrom
  const healthToParam = params.get("account_health_to") || params.get("health_to")
  const parsedHealthTo = healthToParam ? parseInt(healthToParam, 10) : null
  const healthTo = parsedHealthTo !== null && Number.isNaN(parsedHealthTo) ? null : parsedHealthTo
  const healthIsEmpty = (params.get("account_health_empty") || params.get("health_empty")) === "true"

  // When "has any value" is set, clear range/empty so only one health mode is active
  const effectiveHealthHasAnyValue = healthHasAnyValue
  const effectiveHealthFrom = healthHasAnyValue ? null : healthFrom
  const effectiveHealthTo = healthHasAnyValue ? null : healthTo
  const effectiveHealthIsEmpty = healthHasAnyValue ? false : healthIsEmpty

  // Parse owner IDs array
  const ownerIdsParam = params.get("account_owner_ids") || params.get("owner_ids")
  const ownerIds = ownerIdsParam
    ? ownerIdsParam
        .split("|")
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id))
    : []

  // Parse search query
  const searchQuery = params.get("account_query") || params.get("query") || ""

  return {
    status,
    healthFrom: effectiveHealthFrom,
    healthTo: effectiveHealthTo,
    healthIsEmpty: effectiveHealthIsEmpty,
    healthHasAnyValue: effectiveHealthHasAnyValue,
    ownerIds,
    searchQuery,
  }
}

const updateFiltersInURL = (filters: EmailAccountFilters) => {
  if (typeof window === "undefined") return

  const params = new URLSearchParams(window.location.search)

  // Update status
  if (filters.status.length > 0) {
    params.set("account_status", filters.status.join("|"))
  } else {
    params.delete("account_status")
    params.delete("status")
  }

  // Update health range
  if (filters.healthFrom !== null) {
    params.set("account_health_from", filters.healthFrom.toString())
  } else {
    params.delete("account_health_from")
    params.delete("health_from")
  }

  if (filters.healthTo !== null) {
    params.set("account_health_to", filters.healthTo.toString())
  } else {
    params.delete("account_health_to")
    params.delete("health_to")
  }

  if (filters.healthIsEmpty) {
    params.set("account_health_empty", "true")
  } else {
    params.delete("account_health_empty")
    params.delete("health_empty")
  }

  // Update health "has any value" (mutually exclusive with range/empty)
  if (filters.healthHasAnyValue) {
    params.set("account_health_has_any_value", "true")
    params.delete("account_health_from")
    params.delete("health_from")
    params.delete("account_health_to")
    params.delete("health_to")
    params.delete("account_health_empty")
    params.delete("health_empty")
  } else {
    params.delete("account_health_has_any_value")
    params.delete("health_has_any_value")
  }

  // Update owner IDs
  if (filters.ownerIds.length > 0) {
    params.set("account_owner_ids", filters.ownerIds.join("|"))
  } else {
    params.delete("account_owner_ids")
    params.delete("owner_ids")
  }

  // Update search query
  if (filters.searchQuery) {
    params.set("account_query", filters.searchQuery)
  } else {
    params.delete("account_query")
    params.delete("query")
  }

  // Update URL without reload
  const queryString = params.toString()
  const newURL = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname
  window.history.replaceState(null, "", newURL)
}

const buildDefaultColumns = (
  sharedWorkspace: boolean,
  capacityData: Record<number, CapacityData>,
  loadingCapacity: Set<number>,
  setActiveAccountFn: (account: EmailAccount) => void,
  onRunHealthCheck: (account: EmailAccount) => void,
  onDisconnect: (account: EmailAccount) => void,
  onCancelRenewal: (account: EmailAccount) => void,
  onReconnect: (account: EmailAccount) => void,
  onDelete: (account: EmailAccount) => void,
  isPremium: boolean,
): ColumnDef[] => {
  const columns: ColumnDef[] = [
    {
      id: "email",
      sticky: true,
      renderHeader: () => i18n.t("js.email_accounts_center.columns.email_address"),
      renderCell: (account) => (
        <Button
          variant="ghost"
          size="xs"
          className="lead-tag lead-tag--people"
          type="button"
          onClick={() => setActiveAccountFn(account)}
        >
          <img className="lead-tag__avatar p-05" src={providerIconMap[account.provider] || smtpImapIcon} alt="" />
          <div className="lead-tag__name">{account.email}</div>
        </Button>
      ),
    },
    {
      id: "sender_name",
      renderHeader: () => i18n.t("js.email_accounts_center.columns.sender_name"),
      renderCell: (account) => <span className="text-xs">{account.display_name || "-"}</span>,
    },
    {
      id: "status",
      renderHeader: () => i18n.t("js.email_accounts_center.columns.status"),
      renderCell: (account) => {
        const getStatusVariant = () => {
          if (account.status === "active") return "success"
          if (account.status === "pending") return "warning"
          return "danger"
        }

        const getStatusLabel = () => {
          if (account.status === "active") return i18n.t("js.email_accounts_center.status.active")
          if (account.status === "pending")
            return i18n.t("js.email_accounts_center.status.pending", {
              default: "Pending",
            })
          return i18n.t("js.email_accounts_center.status.disconnected")
        }

        return (
          <Tag variant={getStatusVariant()}>
            <TagLabel>{getStatusLabel()}</TagLabel>
          </Tag>
        )
      },
    },
    {
      id: "capacity",
      renderHeader: () => i18n.t("js.email_accounts_center.columns.sends_limit"),
      renderCell: (account) => {
        const isLoading = loadingCapacity.has(account.id)
        const capacity = capacityData[account.id]
        const sent = capacity?.sent ?? 0
        const limit = capacity?.limit ?? 0
        const scheduled = capacity?.scheduled ?? 0

        if (isLoading && !capacity) {
          return (
            <Skeleton
              size="freeform"
              style={{ width: "8rem", height: "1.6rem" }}
              aria-label={i18n.t("js.data_table.loading")}
            />
          )
        }

        return <EmailCapacityBreakdown current={sent} total={limit} secondary={scheduled} showSecondary={true} />
      },
    },
    {
      id: "scheduled",
      renderHeader: () => i18n.t("js.email_accounts_center.columns.scheduled"),
      renderCell: (account) =>
        account.scheduled > 0 ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <a className="h-data-table-cell__link" href={campaigns_queue_index_path()}>
                  {i18n.t("js.email_accounts_center.scheduled_count", {
                    count: account.scheduled,
                  })}
                </a>
              </TooltipTrigger>
              <TooltipContent>
                <span
                  dangerouslySetInnerHTML={{
                    __html: i18n.t("js.email_accounts_center.scheduled_tooltip_html", {
                      count: account.scheduled,
                      campaigns_count: 1,
                    }),
                  }}
                />
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span className="text-xs text-body-secondary">
            {i18n.t("js.email_accounts_center.scheduled_count", { count: 0 })}
          </span>
        ),
    },
  ]

  if (isPremium) {
    columns.push({
      id: "health",
      renderHeader: () => i18n.t("js.email_accounts_center.columns.health"),
      renderCell: (account) => (
        <>
          <HealthScoreIndicator score={account.health} />
          <DataTableCellActions>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="xs"
                    type="button"
                    iconOnly
                    onClick={() => onRunHealthCheck(account)}
                    aria-label={i18n.t("js.email_accounts_center.actions.run_health_check")}
                  >
                    <Icon icon="shield-check" size="sm" aria-hidden="true" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{i18n.t("js.email_accounts_center.actions.run_health_check")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </DataTableCellActions>
        </>
      ),
    })
  }

  if (sharedWorkspace) {
    columns.push({
      id: "owner",
      renderHeader: () => i18n.t("js.email_accounts_center.columns.owner"),
      renderCell: (account) => (
        <Avatar size="sm">
          <AvatarFallback>{getInitials(account.owner_name)}</AvatarFallback>
        </Avatar>
      ),
    })
  }

  columns.push({
    id: "actions",
    renderHeader: () => null,
    renderCell: (account) => {
      // Disable all actions for pending accounts
      const isPending = account.status === "pending"

      const moreActions: {
        label: string
        onClick: () => void
        variant?: "danger"
        disabled?: boolean
      }[] = [
        {
          label: i18n.t("js.email_accounts_center.actions.run_health_check"),
          onClick: () => onRunHealthCheck(account),
          disabled: isPending,
        },
      ]

      if (account.status === "active") {
        // Active accounts: show disconnect option
        moreActions.push({
          label: i18n.t("js.email_accounts_center.actions.disconnect"),
          onClick: () => onDisconnect(account),
          variant: "danger" as const,
          disabled: isPending,
        })

        // Only include "Cancel renewal" for active managed accounts
        if (account.managed && !isPending) {
          moreActions.push({
            label: i18n.t("js.email_accounts_center.actions.cancel_renewal"),
            onClick: () => onCancelRenewal(account),
            variant: "danger" as const,
          })
        }
      } else {
        // Disconnected accounts: show reconnect and delete options
        moreActions.push({
          label: i18n.t("js.email_accounts_center.actions.reconnect"),
          onClick: () => onReconnect(account),
          disabled: isPending,
        })
        moreActions.push({
          label: i18n.t("js.email_accounts_center.actions.delete"),
          onClick: () => onDelete(account),
          variant: "danger" as const,
          disabled: isPending,
        })
      }

      return (
        <ActionsCell
          primaryAction={{
            label: i18n.t("js.email_accounts_center.actions.view"),
            onClick: () => setActiveAccountFn(account),
          }}
          moreActions={moreActions}
          moreActionsAriaLabel={i18n.t("js.email_accounts_center.actions.more_actions_aria")}
        />
      )
    },
  })

  return columns
}

export default function EmailAccountsTable({
  total,
  teamMembers,
  sharedWorkspace = false,
  onOpenConnectSidepanel,
  onEmailAccountIdChange,
  initialEmailAccountId,
  allowEmailProvisioning = false,
  isPremium = false,
  isWarmupEnabled = false,
  stripePublicKey,
  creditCardInfo,
  cardholderName,
  billingInfo,
  userEmail,
  pricePerWarmup,
  maxEmailAccounts,
}: Props) {
  const [data, setData] = useState<EmailAccountsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [capacityData, setCapacityData] = useState<Record<number, CapacityData>>({})
  const [loadingCapacity, setLoadingCapacity] = useState<Set<number>>(new Set())
  const [columnOrder, setColumnOrder] = useState<ColumnId[] | null>(null)
  const [filters, setFilters] = useState<EmailAccountFilters>(() => getFiltersFromLocation())
  const [activeAccount, setActiveAccount] = useState<EmailAccount | null>(null)
  const [bulkEditOpen, setBulkEditOpen] = useState(false)
  const [bulkEditLoading, setBulkEditLoading] = useState(false)
  const [bulkEditError, setBulkEditError] = useState<string | null>(null)
  const [bulkEditFlags, setBulkEditFlags] = useState<{
    accountIds: number[]
    canEditSenderName: boolean
    canEditSignature: boolean
    canEditManagedFields: boolean
    count: number
  }>({
    accountIds: [],
    canEditSenderName: false,
    canEditSignature: false,
    canEditManagedFields: false,
    count: 0,
  })
  const [bulkEditData, setBulkEditData] = useState({
    daily_limit: 0,
    default_account: false,
    sender_name: "",
    signature: "",
    managedMailbox: {
      first_name: "",
      last_name: "",
    },
  })
  const [confirmBulkDisconnectOpen, setConfirmBulkDisconnectOpen] = useState(false)
  const [confirmBulkCancelRenewalOpen, setConfirmBulkCancelRenewalOpen] = useState(false)
  const [inboxProtectionModalOpen, setInboxProtectionModalOpen] = useState(false)
  const [inboxProtectionAccountIds, setInboxProtectionAccountIds] = useState<number[]>([])
  const [confirmIndividualDisconnectOpen, setConfirmIndividualDisconnectOpen] = useState(false)
  const [confirmIndividualCancelRenewalOpen, setConfirmIndividualCancelRenewalOpen] = useState(false)
  const [pendingAction, setPendingAction] = useState<{
    type: "bulkDisconnect" | "bulkCancelRenewal" | "individualDisconnect" | "individualCancelRenewal"
    account?: EmailAccount
  } | null>(null)
  const bulkDisconnectDialogRef = useRef<HTMLDialogElement>(null)
  const bulkCancelRenewalDialogRef = useRef<HTMLDialogElement>(null)
  const individualDisconnectDialogRef = useRef<HTMLDialogElement>(null)
  const individualCancelRenewalDialogRef = useRef<HTMLDialogElement>(null)
  const activeAccountRef = useRef<EmailAccount | null>(null)

  const fetchData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true)
      }
      const response = await fetch(email_accounts_path({ format: "json" }), {
        headers: {
          Accept: "application/json",
        },
        credentials: "same-origin",
      })

      if (!response.ok) {
        throw new Error(
          i18n.t("js.email_accounts_center.errors.http_error", {
            status: response.status,
          }),
        )
      }

      const json = await response.json()
      setData(json)
      setError(null)
      return json
    } catch (err) {
      setError(err instanceof Error ? err.message : i18n.t("js.email_accounts_center.errors.load_failed"))
      return null
    } finally {
      if (showLoading) {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Keep ref in sync for use in async callbacks (e.g. ActionCable) where we need current value without running side-effects inside state updaters
  useEffect(() => {
    activeAccountRef.current = activeAccount
  }, [activeAccount])

  // Single place for opening an account: analytics + state + URL sync
  const openAccount = useCallback(
    (account: EmailAccount) => {
      App.analytics.posthog_capture("Email account center: Email account sidepanel open", {
        email_account_id: account.id,
      })
      setActiveAccount(account)
      onEmailAccountIdChange?.(account.id ?? null)
    },
    [onEmailAccountIdChange],
  )

  // Open sidepanel for initialEmailAccountId when data has loaded (e.g. after OAuth redirect with ?email_account_id=latest)
  useEffect(() => {
    if (!data || initialEmailAccountId == null) return

    const allAccounts = [...(data.connected || []), ...(data.managed || [])].filter(
      (a): a is EmailAccount & { id: number } => a.id != null,
    )

    if (allAccounts.length === 0) return

    const account =
      initialEmailAccountId === -1
        ? allAccounts.reduce((latest, a) => (a.id > latest.id ? a : latest))
        : allAccounts.find((a) => a.id === initialEmailAccountId)

    if (account) {
      openAccount(account)
    }
  }, [data, initialEmailAccountId, openAccount])

  const handleSetActiveAccount = useCallback(
    (account: EmailAccount) => {
      openAccount(account)
    },
    [openAccount],
  )

  const handleIndividualRunHealthCheck = useCallback(async (account: EmailAccount) => {
    try {
      const response = await fetch(email_domains_health_checks_path(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({
          email_account_ids: [account.id],
        }),
      })

      if (response.ok) {
        if (App?.displayFlash) {
          App.displayFlash("success", i18n.t("js.email_accounts_center.bulk.health_check_started"))
        }
      } else {
        const error = await response.json()
        alert(error.error || i18n.t("js.email_accounts_center.bulk.health_check_error"))
      }
    } catch (err) {
      alert(i18n.t("js.email_accounts_center.bulk.health_check_error"))
    }
  }, [])

  const handleIndividualDisconnect = useCallback((account: EmailAccount) => {
    setPendingAction({ type: "individualDisconnect", account })
    setConfirmIndividualDisconnectOpen(true)
  }, [])

  const executeIndividualDisconnect = useCallback(async (account: EmailAccount) => {
    try {
      const response = await fetch(email_account_path(account.id!), {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
      })

      if (response.ok) {
        App?.displayFlash(
          "success",
          i18n.t("js.email_accounts_center.bulk.disconnect_success", {
            count: 1,
          }),
        )
        // Table will refresh automatically via ActionCable
      } else {
        const error = await response.json()
        alert(error.error || i18n.t("js.email_accounts_center.bulk.disconnect_error"))
      }
    } catch (err) {
      alert(i18n.t("js.email_accounts_center.bulk.disconnect_error"))
    }
  }, [])

  const handleIndividualCancelRenewal = useCallback((account: EmailAccount) => {
    setPendingAction({ type: "individualCancelRenewal", account })
    setConfirmIndividualCancelRenewalOpen(true)
  }, [])

  const executeIndividualCancelRenewal = useCallback(async (account: EmailAccount) => {
    try {
      const response = await fetch(subscription_email_account_path(account.id!), {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
      })

      if (response.ok) {
        App?.displayFlash(
          "success",
          i18n.t("js.email_accounts_center.bulk.cancel_renewal_success", {
            count: 1,
          }),
        )
        // Table will refresh automatically via ActionCable
      } else {
        const error = await response.json()
        if (App?.displayFlash) {
          App.displayFlash("error", error.error || i18n.t("js.email_accounts_center.bulk.cancel_renewal_error"))
        }
      }
    } catch (err) {
      if (App?.displayFlash) {
        App.displayFlash("error", i18n.t("js.email_accounts_center.bulk.cancel_renewal_error"))
      }
    }
  }, [])

  const handleIndividualReconnect = useCallback(
    async (account: EmailAccount) => {
      await reconnectEmailAccount(account, {
        onSuccess: () => fetchData(false),
      })
    },
    [fetchData],
  )

  const handleIndividualDelete = useCallback(async (account: EmailAccount) => {
    await deleteEmailAccount(account.id, {
      confirmKey: "js.email_accounts_center.actions.delete_confirm",
      successKey: "js.email_accounts_center.actions.delete_success",
      errorKey: "js.email_accounts_center.actions.delete_error",
    })
  }, [])

  const defaultColumns = useMemo(
    () =>
      buildDefaultColumns(
        sharedWorkspace,
        capacityData,
        loadingCapacity,
        handleSetActiveAccount,
        handleIndividualRunHealthCheck,
        handleIndividualDisconnect,
        handleIndividualCancelRenewal,
        handleIndividualReconnect,
        handleIndividualDelete,
        isPremium,
      ),
    [
      sharedWorkspace,
      capacityData,
      loadingCapacity,
      handleSetActiveAccount,
      handleIndividualRunHealthCheck,
      handleIndividualDisconnect,
      handleIndividualCancelRenewal,
      handleIndividualReconnect,
      handleIndividualDelete,
      isPremium,
    ],
  )

  const columns = useMemo(() => {
    if (columnOrder === null) {
      // Initialize from localStorage on first render
      if (typeof window === "undefined") {
        return defaultColumns
      }

      try {
        const stored = window.localStorage.getItem(STORAGE_KEY)
        if (!stored) return defaultColumns

        const storedOrder = JSON.parse(stored) as ColumnId[]
        const byId = Object.fromEntries(defaultColumns.map((column) => [column.id, column]))

        const ordered = storedOrder.map((id) => byId[id]).filter((column): column is ColumnDef => Boolean(column))

        const missing = defaultColumns.filter((column) => !ordered.find((existing) => existing.id === column.id))

        return [...ordered, ...missing]
      } catch {
        return defaultColumns
      }
    }

    // Use stored order when available
    const byId = Object.fromEntries(defaultColumns.map((column) => [column.id, column]))
    const ordered = columnOrder.map((id) => byId[id]).filter((column): column is ColumnDef => Boolean(column))

    const missing = defaultColumns.filter((column) => !ordered.find((existing) => existing.id === column.id))

    return [...ordered, ...missing]
  }, [defaultColumns, columnOrder])

  const [draggingColumnId, setDraggingColumnId] = useState<ColumnId | null>(null)
  const cableRef = useRef<ActionCable.Cable | null>(null)
  const subscriptionRef = useRef<ActionCable.Channel | null>(null)

  // Handle dialog open/close
  useEffect(() => {
    if (confirmBulkDisconnectOpen) {
      bulkDisconnectDialogRef.current?.showModal()
    } else {
      bulkDisconnectDialogRef.current?.close()
    }
  }, [confirmBulkDisconnectOpen])

  useEffect(() => {
    if (confirmBulkCancelRenewalOpen) {
      bulkCancelRenewalDialogRef.current?.showModal()
    } else {
      bulkCancelRenewalDialogRef.current?.close()
    }
  }, [confirmBulkCancelRenewalOpen])

  useEffect(() => {
    if (confirmIndividualDisconnectOpen) {
      individualDisconnectDialogRef.current?.showModal()
    } else {
      individualDisconnectDialogRef.current?.close()
    }
  }, [confirmIndividualDisconnectOpen])

  useEffect(() => {
    if (confirmIndividualCancelRenewalOpen) {
      individualCancelRenewalDialogRef.current?.showModal()
    } else {
      individualCancelRenewalDialogRef.current?.close()
    }
  }, [confirmIndividualCancelRenewalOpen])

  const bulkEditAccount = useMemo<EmailAccount>(
    () => ({
      id: -1,
      email: i18n.t("js.email_accounts_center.bulk.edit_title", {
        count: bulkEditFlags.count,
      }),
      status: "active",
      sent: 0,
      limit: 0,
      scheduled: 0,
      health: null,
      protection: "",
      owner_id: 0,
      owner_name: "",
      provider: "bulk",
      managed: bulkEditFlags.canEditManagedFields,
      _bulkEdit: {
        accountIds: bulkEditFlags.accountIds,
        canEditSenderName: bulkEditFlags.canEditSenderName,
        canEditSignature: bulkEditFlags.canEditSignature,
        canEditManagedFields: bulkEditFlags.canEditManagedFields,
      },
    }),
    [bulkEditFlags],
  )

  // Subscribe for push updates to account health and disconnections
  useEffect(() => {
    if (subscriptionRef.current) return

    const consumer = ActionCable.createConsumer()
    cableRef.current = consumer

    // Subscribe to health checks channel
    const healthSubscription = consumer.subscriptions.create(
      { channel: "HealthChecksChannel" },
      {
        received: (data: { domain: string; health: number | null; status?: string | null }) => {
          const domain = data.domain?.toLowerCase()
          if (!domain) return

          const updateAccounts = (accounts: EmailAccount[] | undefined) =>
            accounts?.map((acc) => {
              const accDomain = acc.email.split("@")[1]?.toLowerCase()
              return accDomain === domain ? { ...acc, health: data.health } : acc
            })

          setData((prev) => {
            if (!prev) return prev
            return {
              ...prev,
              connected: updateAccounts(prev.connected) ?? prev.connected,
              managed: updateAccounts(prev.managed) ?? prev.managed,
            }
          })

          setActiveAccount((prev) => {
            if (!prev) return prev
            const accDomain = prev.email.split("@")[1]?.toLowerCase()
            return accDomain === domain ? { ...prev, health: data.health } : prev
          })
        },
      },
    )

    // Subscribe to email accounts channel for disconnections and updates
    const emailAccountsSubscription = consumer.subscriptions.create(
      { channel: "EmailAccountsChannel" },
      {
        received: (data: { type: string; count?: number }) => {
          if (data.type === "accounts_disconnected" || data.type === "subscription_canceled") {
            // Refresh data when accounts are disconnected or subscriptions are cancelled
            fetchData(false).then((freshData) => {
              const current = activeAccountRef.current
              if (!current) return

              const allAccounts = [...(freshData?.connected || []), ...(freshData?.managed || [])]
              const accountStillExists = allAccounts.some((acc) => acc.id === current.id)
              if (!accountStillExists) {
                // Account was disconnected, close the sidepanel and clear URL (outside state updater to avoid duplicate calls)
                setActiveAccount(null)
                onEmailAccountIdChange?.(null)
              }
            })
          }
        },
      },
    )

    return () => {
      healthSubscription.unsubscribe()
      emailAccountsSubscription.unsubscribe()
      consumer.disconnect()
      subscriptionRef.current = null
      cableRef.current = null
    }
  }, [fetchData, onEmailAccountIdChange])

  // Fetch capacity data for all accounts in a single request
  const fetchCapacities = useCallback(async () => {
    try {
      const response = await fetch(capacities_email_accounts_path({ format: "json" }), {
        headers: {
          Accept: "application/json",
        },
        credentials: "same-origin",
      })

      if (response.ok) {
        const capacities = await response.json()
        setCapacityData(capacities)
      }
    } catch (err) {
      // Silently fail - capacity data is not critical
      console.error("Failed to fetch capacity data:", err)
    }
  }, [])

  // Fetch capacity data for a single account (used for real-time updates)
  const fetchCapacityData = useCallback(async (accountId: number) => {
    try {
      setLoadingCapacity((prev) => new Set(prev).add(accountId))
      const response = await fetch(email_account_path(accountId, { format: "json" }), {
        headers: {
          Accept: "application/json",
        },
        credentials: "same-origin",
      })

      if (response.ok) {
        const json = await response.json()
        const capacity: CapacityData = {
          sent: json.sent_in_current_daily_limit ?? 0,
          limit: json.limit ?? 0,
          scheduled: json.scheduled ?? 0,
        }
        setCapacityData((prev) => ({
          ...prev,
          [accountId]: capacity,
        }))

        // Also update display_name in accounts state if it changed
        if (json.display_name !== undefined) {
          setData((prev) => {
            if (!prev) return prev

            const updateAccount = (account: EmailAccount) =>
              account.id === accountId ? { ...account, display_name: json.display_name } : account

            return {
              ...prev,
              connected: prev.connected?.map(updateAccount),
              managed: prev.managed?.map(updateAccount),
            }
          })
        }
      }
    } catch (err) {
      // Silently fail - capacity data is not critical
      console.error(`Failed to fetch capacity data for account ${accountId}:`, err)
    } finally {
      setLoadingCapacity((prev) => {
        const next = new Set(prev)
        next.delete(accountId)
        return next
      })
    }
  }, [])

  const handleUsageUpdate = useCallback(
    (accountId: number) => {
      // Refetch capacity for this specific account (also updates display_name)
      fetchCapacityData(accountId)
    },
    [fetchCapacityData],
  )

  useEmailAccountUsage({ onUpdate: handleUsageUpdate })

  // Fetch all capacity data when accounts data is loaded
  useEffect(() => {
    if (!data) return

    fetchCapacities()
  }, [data, fetchCapacities])

  // Sync filters to URL whenever they change
  useEffect(() => {
    updateFiltersInURL(filters)
  }, [filters])

  // Filter accounts based on filter state and search query - MUST be called before any early returns
  const filteredData = useMemo(() => {
    return filterEmailAccounts(data, filters)
  }, [data, filters])

  // Transform data to use unique IDs for pending accounts (id is null)
  const transformedData = useMemo(() => {
    if (!filteredData) return null

    const transformAccount = (account: EmailAccount) => ({
      ...account,
      id: account.id ?? (`pending:${account.email}` as string | number),
    })

    return {
      connected: filteredData.connected?.map(transformAccount),
      managed: filteredData.managed?.map(transformAccount),
      total: filteredData.total,
    }
  }, [filteredData])

  // Use the transformed data for selection
  const {
    selectedIds,
    selectedCount,
    toggleSelection,
    clearSelection,
    selectAll,
    allItems,
    connectedItems,
    managedItems,
    isSelected,
  } = useTableSelection(transformedData)

  // Helper to extract numeric IDs from selectedIds (filter out pending account string IDs)
  const getNumericIds = useCallback((ids: Array<string | number>): number[] => {
    return ids.filter((id): id is number => typeof id === "number")
  }, [])

  // Helper to get non-pending items (filter out pending accounts)
  const getNonPendingItems = useCallback((items: typeof allItems) => {
    return items.filter((account) => account.status !== "pending")
  }, [])

  // Override isAllSelected to only consider non-pending items
  const nonPendingItems = useMemo(() => getNonPendingItems(allItems), [allItems, getNonPendingItems])
  const isAllNonPendingSelected = useMemo(
    () => nonPendingItems.length > 0 && nonPendingItems.every((item) => selectedIds.includes(item.id)),
    [nonPendingItems, selectedIds],
  )

  // Override section selection to only consider non-pending items
  const nonPendingConnectedItems = useMemo(
    () => getNonPendingItems(connectedItems),
    [connectedItems, getNonPendingItems],
  )
  const nonPendingManagedItems = useMemo(() => getNonPendingItems(managedItems), [managedItems, getNonPendingItems])

  const isConnectedSectionNonPendingSelected = useMemo(
    () =>
      nonPendingConnectedItems.length > 0 && nonPendingConnectedItems.every((item) => selectedIds.includes(item.id)),
    [nonPendingConnectedItems, selectedIds],
  )

  const isManagedSectionNonPendingSelected = useMemo(
    () => nonPendingManagedItems.length > 0 && nonPendingManagedItems.every((item) => selectedIds.includes(item.id)),
    [nonPendingManagedItems, selectedIds],
  )

  const selectNonPendingSection = useCallback(
    (section: "connected" | "managed") => {
      const sectionItems = section === "connected" ? nonPendingConnectedItems : nonPendingManagedItems
      const sectionIds = sectionItems.map((item) => item.id)
      const allSelected = sectionIds.length > 0 && sectionIds.every((id) => selectedIds.includes(id))

      if (allSelected) {
        // Deselect all items in this section - keep other selections
        const otherSelectedIds = selectedIds.filter((id) => !sectionIds.includes(id))
        const otherSelectedItems = allItems.filter((item) => otherSelectedIds.includes(item.id))
        selectAll(otherSelectedItems)
      } else {
        // Select all non-pending items in this section (merge with existing selections)
        const currentSelectedIds = new Set(selectedIds)
        sectionIds.forEach((id) => currentSelectedIds.add(id))
        const allSelectedItems = allItems.filter((item) => currentSelectedIds.has(item.id))
        selectAll(allSelectedItems)
      }
    },
    [nonPendingConnectedItems, nonPendingManagedItems, selectedIds, allItems, selectAll],
  )

  const handleRunHealthCheck = async () => {
    const numericIds = getNumericIds(selectedIds)
    if (numericIds.length === 0) return

    try {
      const response = await fetch(email_domains_health_checks_path(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({
          email_account_ids: numericIds,
        }),
      })

      if (response.ok) {
        clearSelection()
      } else {
        const error = await response.json()
        alert(error.error || i18n.t("js.email_accounts_center.bulk.health_check_error"))
      }
    } catch (err) {
      alert(i18n.t("js.email_accounts_center.bulk.health_check_error"))
    }
  }

  const handleLearnMoreWarmup = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }, [])

  const warmupEligibleIds = useMemo(() => {
    const selectedAccounts = allItems.filter(
      (account) => selectedIds.includes(account.id) && typeof account.id === "number",
    )

    return selectedAccounts
      .filter((account) => getWarmupStateFromProtection(account.protection) !== "warming_up")
      .map((account) => account.id as number)
  }, [allItems, selectedIds])

  const handleEnableInboxProtection = useCallback(() => {
    if (!isWarmupEnabled || warmupEligibleIds.length === 0) return
    setInboxProtectionAccountIds(warmupEligibleIds)
    setInboxProtectionModalOpen(true)
  }, [isWarmupEnabled, warmupEligibleIds])

  const handleEdit = () => {
    if (selectedIds.length === 0) return

    const selectedAccounts = allItems.filter((account) => selectedIds.includes(account.id))
    if (selectedAccounts.length === 0) return

    const allAreManaged = selectedAccounts.every((acc) => acc.managed)
    const allCanEditSenderName = selectedAccounts.every(
      (acc) => acc.provider !== "outlook" && acc.provider !== "smtp_imap",
    )
    const allCanEditSignature = selectedAccounts.every((acc) => acc.managed || acc.provider === "smtp_imap")

    // Only allow bulk edit for accounts with numeric IDs (exclude pending)
    const numericIds = getNumericIds(selectedIds)
    if (numericIds.length === 0) return

    setBulkEditFlags({
      accountIds: numericIds,
      canEditSenderName: allCanEditSenderName,
      canEditSignature: allCanEditSignature,
      canEditManagedFields: allAreManaged,
      count: numericIds.length,
    })
    setBulkEditData({
      daily_limit: 0,
      default_account: false,
      sender_name: "",
      signature: "",
      managedMailbox: {
        first_name: "",
        last_name: "",
      },
    })
    setBulkEditError(null)
    setBulkEditOpen(true)
  }

  const handleDisconnect = useCallback(() => {
    if (selectedIds.length === 0) return
    setPendingAction({ type: "bulkDisconnect" })
    setConfirmBulkDisconnectOpen(true)
  }, [selectedIds])

  const executeBulkDisconnect = useCallback(async () => {
    const numericIds = getNumericIds(selectedIds)
    if (numericIds.length === 0) return

    try {
      const response = await fetch(bulk_email_accounts_path(), {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({
          ids: numericIds,
        }),
      })

      if (response.status === 202) {
        App?.displayFlash(
          "success",
          i18n.t("js.email_accounts_center.bulk.disconnect_queued", {
            count: numericIds.length,
          }),
        )
        clearSelection()
      } else if (response.ok) {
        const result = await response.json()
        if (result.errors && result.errors.length > 0) {
          const errorMessages = result.errors
            .map((err: { id: number; errors: string[] }) => err.errors.join(", "))
            .join("\n")
          alert(
            i18n.t("js.email_accounts_center.bulk.disconnect_partial_error", {
              count: result.destroyed_count,
            }) +
              "\n\n" +
              errorMessages,
          )
        } else {
          if (App?.displayFlash) {
            App.displayFlash(
              "success",
              i18n.t("js.email_accounts_center.bulk.disconnect_success", {
                count: result.destroyed_count || numericIds.length,
              }),
            )
          }
        }
        clearSelection()
      } else {
        const error = await response.json()
        alert(error.error || i18n.t("js.email_accounts_center.bulk.disconnect_error"))
      }
    } catch (err) {
      alert(i18n.t("js.email_accounts_center.bulk.disconnect_error"))
    }
  }, [selectedIds, clearSelection])

  const handleCancelRenewal = useCallback(() => {
    const numericIds = getNumericIds(selectedIds)
    if (numericIds.length === 0) return
    setPendingAction({ type: "bulkCancelRenewal" })
    setConfirmBulkCancelRenewalOpen(true)
  }, [selectedIds, getNumericIds])

  const executeBulkCancelRenewal = useCallback(async () => {
    const numericIds = getNumericIds(selectedIds)
    if (numericIds.length === 0) return

    try {
      const response = await fetch(bulk_subscription_email_accounts_path(), {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({
          ids: numericIds,
        }),
      })

      if (response.status === 202) {
        App?.displayFlash(
          "success",
          i18n.t("js.email_accounts_center.bulk.cancel_renewal_queued", {
            count: numericIds.length,
          }),
        )
        clearSelection()
      } else if (response.ok) {
        const result = await response.json()
        if (result.errors && result.errors.length > 0) {
          const errorMessages = result.errors
            .map((err: { id: number; errors: string[] }) => err.errors.join(", "))
            .join("\n")
          alert(
            i18n.t("js.email_accounts_center.bulk.cancel_renewal_partial_error", {
              count: result.cancelled_count,
            }) +
              "\n\n" +
              errorMessages,
          )
        } else {
          if (App?.displayFlash) {
            App.displayFlash(
              "success",
              i18n.t("js.email_accounts_center.bulk.cancel_renewal_success", {
                count: result.cancelled_count || numericIds.length,
              }),
            )
          }
        }
        clearSelection()
      } else {
        const error = await response.json()
        if (App?.displayFlash) {
          App.displayFlash("error", error.error || i18n.t("js.email_accounts_center.bulk.cancel_renewal_error"))
        }
      }
    } catch (err) {
      if (App?.displayFlash) {
        App.displayFlash("error", i18n.t("js.email_accounts_center.bulk.cancel_renewal_error"))
      }
    }
  }, [selectedIds, clearSelection])

  const handleBulkEditSubmit = async () => {
    if (bulkEditFlags.accountIds.length === 0) return

    setBulkEditLoading(true)
    setBulkEditError(null)

    try {
      const emailAccountPayload: Record<string, unknown> = {}

      if (bulkEditFlags.canEditSenderName && bulkEditData.sender_name.trim()) {
        emailAccountPayload.display_name = bulkEditData.sender_name.trim()
      }

      if (bulkEditData.daily_limit > 0) {
        emailAccountPayload.daily_messages_limit = bulkEditData.daily_limit
      }

      if (bulkEditFlags.canEditSignature && (bulkEditData.signature || "").trim()) {
        emailAccountPayload.signature = bulkEditData.signature.trim()
      }

      const managedMailboxPayload: Record<string, string> = {}
      if (bulkEditFlags.canEditManagedFields) {
        if (bulkEditData.managedMailbox.first_name.trim()) {
          managedMailboxPayload.first_name = bulkEditData.managedMailbox.first_name.trim()
        }
        if (bulkEditData.managedMailbox.last_name.trim()) {
          managedMailboxPayload.last_name = bulkEditData.managedMailbox.last_name.trim()
        }
      }

      const body: Record<string, unknown> = {
        ids: bulkEditFlags.accountIds,
        email_account: emailAccountPayload,
      }

      if (bulkEditFlags.canEditManagedFields && Object.keys(managedMailboxPayload).length > 0) {
        body.managed_mailbox = managedMailboxPayload
      }

      const response = await fetch(bulk_email_accounts_path(), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify(body),
      })

      if (response.ok) {
        const result = await response.json()
        const updatedCount = result.updated_count || bulkEditFlags.accountIds.length

        setBulkEditOpen(false)
        clearSelection()
        setBulkEditData({
          daily_limit: 0,
          default_account: false,
          sender_name: "",
          signature: "",
          managedMailbox: { first_name: "", last_name: "" },
        })

        App.displayFlash(
          "success",
          i18n.t("js.email_accounts_center.bulk.edit_success", {
            count: updatedCount,
          }),
        )

        const dataResponse = await fetch(email_accounts_path({ format: "json" }), {
          headers: {
            Accept: "application/json",
          },
          credentials: "same-origin",
        })
        if (dataResponse.ok) {
          const json = await dataResponse.json()
          setData(json)
        }
      } else {
        const error = await response.json()
        const errorMessage = error.error || i18n.t("js.email_accounts_center.bulk.edit_error")
        setBulkEditError(errorMessage)
        App.displayFlash("error", errorMessage)
      }
    } catch (err) {
      const errorMessage = i18n.t("js.email_accounts_center.bulk.edit_error")
      setBulkEditError(errorMessage)
      App.displayFlash("error", errorMessage)
    } finally {
      setBulkEditLoading(false)
    }
  }

  const handleHeaderDragStart = (event: React.DragEvent, id: ColumnId) => {
    setDraggingColumnId(id)
    event.dataTransfer.effectAllowed = "move"
  }

  const handleHeaderDragOver = (event: React.DragEvent, id: ColumnId) => {
    event.preventDefault()
    if (!draggingColumnId || draggingColumnId === id) return

    setColumnOrder((currentOrder) => {
      if (currentOrder === null) {
        const currentIds = columns.map((column) => column.id)
        currentOrder = currentIds
      }

      const fromIndex = currentOrder.findIndex((columnId) => columnId === draggingColumnId)
      const toIndex = currentOrder.findIndex((columnId) => columnId === id)

      if (fromIndex === -1 || toIndex === -1) return currentOrder

      const updated = [...currentOrder]
      const [moved] = updated.splice(fromIndex, 1)
      updated.splice(toIndex, 0, moved)
      return updated
    })
  }

  const handleHeaderDragEnd = () => {
    setDraggingColumnId(null)

    if (typeof window !== "undefined") {
      const order = columns.map((column) => column.id)
      setColumnOrder(order)
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(order))
    }
  }

  // Compute bulk actions based on selected accounts
  const bulkActions = useMemo(() => {
    // Check if any selected accounts are pending (disable all actions if so)
    const hasPendingAccounts =
      selectedCount > 0 && allItems.some((account) => selectedIds.includes(account.id) && account.status === "pending")

    // Only allow actions on accounts with numeric IDs (exclude pending)
    const numericIds = getNumericIds(selectedIds)
    const hasActionableAccounts = numericIds.length > 0

    const actions = [
      {
        label: i18n.t("js.email_accounts_center.bulk.run_health_check"),
        icon: "fa-shield-check",
        onClick: handleRunHealthCheck,
        disabled: selectedCount === 0 || hasPendingAccounts || !hasActionableAccounts,
      },
      {
        label: i18n.t("js.email_accounts_center.bulk.edit"),
        icon: "fa-pen",
        onClick: handleEdit,
        disabled: selectedCount === 0 || hasPendingAccounts || !hasActionableAccounts,
      },
      {
        label: i18n.t("js.email_accounts_center.actions.disconnect"),
        icon: "fa-unlink",
        onClick: handleDisconnect,
        disabled: selectedCount === 0 || hasPendingAccounts || !hasActionableAccounts,
      },
    ]

    if (isWarmupEnabled) {
      actions.splice(1, 0, {
        label: i18n.t("js.email_accounts_center.bulk.enable_inbox_protection"),
        icon: "fa-shield-check",
        onClick: handleEnableInboxProtection,
        disabled: selectedCount === 0 || hasPendingAccounts || warmupEligibleIds.length === 0,
      })
    }

    // Only include "Cancel renewal" if all selected accounts are managed and not pending
    if (selectedCount > 0 && !hasPendingAccounts && hasActionableAccounts) {
      const selectedAccounts = allItems.filter((account) => selectedIds.includes(account.id))
      const allAreManaged = selectedAccounts.every((acc) => acc.managed === true && acc.status !== "pending")

      if (allAreManaged) {
        actions.push({
          label: i18n.t("js.email_accounts_center.actions.cancel_renewal"),
          icon: "fa-times-circle",
          onClick: handleCancelRenewal,
          disabled: selectedCount === 0,
        })
      }
    }

    return actions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedIds,
    selectedCount,
    allItems,
    handleCancelRenewal,
    getNumericIds,
    handleEnableInboxProtection,
    warmupEligibleIds.length,
    isWarmupEnabled,
  ])

  const skeletonColumns = columns
    .filter((column) => column.id !== "actions")
    .map((column) => ({
      label: column.renderHeader(),
      sticky: column.sticky,
    }))

  const renderEmailAccountRow = (account: EmailAccount, section?: string) => {
    // Use email as unique identifier for pending accounts (id is null)
    const uniqueId = account.id ?? `pending:${account.email}`
    const isPending = account.status === "pending"

    return (
      <DataTableRow
        key={uniqueId}
        id={uniqueId}
        className={account.status === "disconnected" ? "h-data-table-row--error" : undefined}
      >
        <DataTableCheckboxCell
          aria-label={i18n.t("js.email_accounts_center.bulk.select_record_aria", {
            email: account.email,
          })}
          recordId={uniqueId}
          section={section}
          checked={isSelected(uniqueId)}
          disabled={isPending}
          onChange={() => toggleSelection(uniqueId)}
        />
        {columns.map((column) => (
          <DataTableCell key={column.id} sticky={column.sticky}>
            {column.renderCell(account)}
          </DataTableCell>
        ))}
      </DataTableRow>
    )
  }

  if (loading) {
    return (
      <div>
        <div className="main-heading">
          <h2 className="main-heading__count">
            <Skeleton size="freeform" style={{ width: "10.4rem", height: "1.6rem" }} />
          </h2>
        </div>

        <DataTableSkeleton
          columns={skeletonColumns}
          checkboxAriaLabel={i18n.t("js.email_accounts_center.bulk.select_all")}
        />
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <div className="main-heading">
          <h2 className="main-heading__count">
            <span
              dangerouslySetInnerHTML={{
                __html: i18n.t("js.email_accounts_center.count_html", {
                  count: 0,
                  pretty_count: "0",
                }),
              }}
            />
          </h2>

          <hr className="main-heading__separator" />

          <Alert variant="destructive" className="mt-3">
            <AlertContent>{i18n.t("js.email_accounts_center.errors.alert")}</AlertContent>
          </Alert>

          <SearchInput
            value={filters.searchQuery || ""}
            onChange={(value) => setFilters((prev) => ({ ...prev, searchQuery: value }))}
            placeholder={i18n.t("js.email_accounts_center.search_placeholder")}
            ariaLabel={i18n.t("js.email_accounts_center.search_aria")}
            clearAriaLabel={i18n.t("js.email_accounts_center.clear_search")}
          />
          <FiltersDropdown
            filters={filters}
            setFilters={setFilters}
            teamMembers={teamMembers}
            sharedWorkspace={sharedWorkspace}
          />
        </div>

        <DataTableSkeleton
          columns={skeletonColumns}
          checkboxAriaLabel={i18n.t("js.email_accounts_center.bulk.select_all")}
        />
      </div>
    )
  }

  const totalCount = filteredData?.total ?? total ?? 0
  const connectedCount = connectedItems.length
  const managedCount = managedItems.length
  const connectedActiveCount = connectedItems.filter((account) => account.status === "active").length
  const managedActiveCount = managedItems.filter((account) => account.status === "active").length

  return (
    <div>
      <div className="main-heading">
        <h2 className="main-heading__count">
          <span
            dangerouslySetInnerHTML={{
              __html: i18n.t("js.email_accounts_center.count_html", {
                pretty_count: CurrencyHelper.numberWithDelimiter(totalCount),
                count: totalCount,
              }),
            }}
          />
        </h2>

        <hr className="main-heading__separator" />

        <div className="main-heading__filtering">
          <SearchInput
            value={filters.searchQuery || ""}
            onChange={(value) => setFilters((prev) => ({ ...prev, searchQuery: value }))}
            placeholder={i18n.t("js.email_accounts_center.search_placeholder")}
            ariaLabel={i18n.t("js.email_accounts_center.search_aria")}
            clearAriaLabel={i18n.t("js.email_accounts_center.clear_search")}
          />
          <FiltersDropdown
            filters={filters}
            setFilters={setFilters}
            teamMembers={teamMembers}
            sharedWorkspace={sharedWorkspace}
          />
        </div>
      </div>

      <FiltersTags
        filters={filters}
        setFilters={setFilters}
        teamMembers={teamMembers}
        sharedWorkspace={sharedWorkspace}
      />

      <DataTable
        bulkActionsBar={
          <DataTableBulkActionsBar
            totalCount={totalCount}
            pageCount={allItems.length}
            selectedCount={selectedCount}
            onClearSelection={clearSelection}
            onSelectAll={() => selectAll(getNonPendingItems(allItems))}
            onSelectPage={() => selectAll(getNonPendingItems(allItems))}
            actions={bulkActions}
          />
        }
      >
        <DataTableHeader>
          <DataTableHeaderCell className="h-data-table__checkbox">
            <div className="h-data-table-cell h-data-table-cell--checkbox">
              <Checkbox
                aria-label={i18n.t("js.email_accounts_center.bulk.select_page")}
                checked={isAllNonPendingSelected}
                onChange={() => selectAll(isAllNonPendingSelected ? [] : getNonPendingItems(allItems))}
              />
            </div>
          </DataTableHeaderCell>
          {columns.map((column) => (
            <DataTableHeaderCell
              key={column.id}
              sticky={column.sticky}
              draggable={!column.sticky}
              onDragStart={column.sticky ? undefined : (event) => handleHeaderDragStart(event, column.id)}
              onDragOver={column.sticky ? undefined : (event) => handleHeaderDragOver(event, column.id)}
              onDragEnd={column.sticky ? undefined : handleHeaderDragEnd}
              className={draggingColumnId === column.id ? "dragging" : undefined}
            >
              {column.renderHeader()}
              {column.id === "email" && (connectedCount === 0 || managedCount === 0) && (
                <span className="text-xs text-hint ml-1">
                  ·{" "}
                  {connectedCount > 0
                    ? `${connectedActiveCount}/${connectedCount}`
                    : `${managedActiveCount}/${managedCount}`}
                </span>
              )}
            </DataTableHeaderCell>
          ))}
        </DataTableHeader>

        <DataTableBody>
          {/* Empty state when no results */}
          {totalCount === 0 && (
            <DataTableEmptyState
              colSpan={columns.length + 1}
              onClearFilters={
                filters.searchQuery ||
                filters.status.length > 0 ||
                filters.healthFrom !== null ||
                filters.healthTo !== null ||
                filters.healthIsEmpty ||
                filters.healthHasAnyValue ||
                filters.ownerIds.length > 0
                  ? () =>
                      setFilters({
                        status: [],
                        healthFrom: null,
                        healthTo: null,
                        healthIsEmpty: false,
                        healthHasAnyValue: false,
                        ownerIds: [],
                        searchQuery: "",
                      })
                  : undefined
              }
            />
          )}

          {/* Connected email accounts group row - only when both types exist */}
          {connectedCount > 0 && (
            <>
              {managedCount > 0 && (
                <DataTableGroupRow
                  colSpan={columns.length + 1}
                  sectionId="connected"
                  checkboxCell={
                    <Checkbox
                      aria-label={i18n.t("js.email_accounts_center.groups.select_connected_group_aria")}
                      checked={isConnectedSectionNonPendingSelected}
                      onChange={() => selectNonPendingSection("connected")}
                    />
                  }
                >
                  <strong className="fw-medium text-body-secondary">
                    {i18n.t("js.email_accounts_center.groups.connected_title")}
                  </strong>
                  <span className="text-xs text-hint">
                    · {connectedActiveCount}/{connectedCount}
                  </span>
                  <Button
                    size="xs"
                    className="ml-auto"
                    onClick={() => {
                      if (onOpenConnectSidepanel) {
                        onOpenConnectSidepanel("connected")
                      }
                    }}
                  >
                    <Icon icon="plus" size="sm" aria-hidden="true" />
                    {i18n.t("js.email_accounts_center.groups.connect_email_account")}
                  </Button>
                </DataTableGroupRow>
              )}

              {connectedItems.map((account) => renderEmailAccountRow(account, "connected"))}
            </>
          )}

          {/* Managed email accounts group row - only when both types exist */}
          {managedCount > 0 && (
            <>
              {connectedCount > 0 && (
                <DataTableGroupRow
                  colSpan={columns.length + 1}
                  sectionId="managed"
                  checkboxCell={
                    <Checkbox
                      aria-label={i18n.t("js.email_accounts_center.groups.select_group_aria")}
                      checked={isManagedSectionNonPendingSelected}
                      onChange={() => selectNonPendingSection("managed")}
                    />
                  }
                >
                  <strong className="fw-medium text-body-secondary mr-1">
                    {i18n.t("js.email_accounts_center.groups.managed_title")}
                  </strong>
                  <span className="text-xs text-hint ml-1">
                    · {managedActiveCount}/{managedCount}
                  </span>
                  {allowEmailProvisioning && (
                    <Button size="xs" className="ml-auto" type="button" asChild>
                      <a href={email_accounts_mailboxes_path()} data-turbo="false">
                        <Icon icon="plus" className="mr-1" aria-hidden="true" />
                        {i18n.t("js.email_accounts_center.groups.create_email_accounts")}
                      </a>
                    </Button>
                  )}
                </DataTableGroupRow>
              )}

              {managedItems.map((account) => renderEmailAccountRow(account, "managed"))}
            </>
          )}
        </DataTableBody>
      </DataTable>

      {/* Individual Account Sidepanel */}
      <Sheet
        open={!!activeAccount}
        modal={false}
        onOpenChange={(open) => {
          // Don't close if a Bootstrap modal is open
          const hasOpenModal = document.querySelector(".modal.show")
          if (!open && !hasOpenModal) {
            setActiveAccount(null)
            onEmailAccountIdChange?.(null)
          }
        }}
      >
        <SheetContent
          side="right"
          size="md"
          className="p-0"
          onEscapeKeyDown={(e) => {
            // Prevent closing on Escape if a Bootstrap modal is open
            const hasOpenModal = document.querySelector(".modal.show")
            if (hasOpenModal) {
              e.preventDefault()
            }
          }}
        >
          {activeAccount && (
            <EmailAccountSidepanel
              account={activeAccount}
              isPremium={isPremium}
              isWarmupEnabled={isWarmupEnabled}
              stripePublicKey={stripePublicKey}
              creditCardInfo={creditCardInfo}
              cardholderName={cardholderName}
              billingInfo={billingInfo}
              userEmail={userEmail}
              pricePerWarmup={pricePerWarmup}
              onAccountUpdated={async (updatedAccount) => {
                // Fetch the show endpoint to get fresh capacity data (sent_in_current_daily_limit, limit, pending_messages)
                try {
                  const response = await fetch(email_account_path(updatedAccount.id!, { format: "json" }), {
                    headers: {
                      Accept: "application/json",
                    },
                    credentials: "same-origin",
                  })

                  if (response.ok) {
                    const showData = await response.json()
                    // Update capacity data from show endpoint response
                    setCapacityData((prev) => ({
                      ...prev,
                      [updatedAccount.id!]: {
                        sent: showData.sent_in_current_daily_limit ?? 0,
                        limit: showData.limit ?? 0,
                        scheduled: showData.pending_messages ?? 0,
                      },
                    }))

                    // Also update display_name in accounts state if it changed
                    if (showData.display_name !== undefined) {
                      setData((prev) => {
                        if (!prev) return prev

                        const updateAccount = (account: EmailAccount) =>
                          account.id === updatedAccount.id
                            ? {
                                ...account,
                                display_name: showData.display_name,
                              }
                            : account

                        return {
                          ...prev,
                          connected: prev.connected?.map(updateAccount),
                          managed: prev.managed?.map(updateAccount),
                        }
                      })
                    }
                  }
                } catch (err) {
                  console.error(`Failed to fetch show data for account ${updatedAccount.id}:`, err)
                }

                // Refresh the table data first to get all updated accounts
                const freshData = await fetchData(false)
                if (freshData) {
                  // Find the updated account in the fresh data
                  const allAccounts = [...(freshData.connected || []), ...(freshData.managed || [])]
                  const freshAccount = allAccounts.find((acc) => acc.id === updatedAccount.id)
                  if (freshAccount) {
                    setActiveAccount(freshAccount)
                  } else {
                    setActiveAccount(updatedAccount)
                  }
                } else {
                  setActiveAccount(updatedAccount)
                }
              }}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Bulk Edit Sheet */}
      <Sheet open={bulkEditOpen} onOpenChange={setBulkEditOpen} modal={false}>
        <SheetContent className="p-0" overlay={false} side="right" size="md">
          <SheetHeader className="d-flex flex-column align-items-start gap-1">
            <SheetTitle className="w-100">
              {i18n.t("js.email_accounts_center.bulk.edit_title", {
                count: bulkEditFlags.count,
              })}
            </SheetTitle>
            <span className="text-body-secondary w-100">
              {i18n.t("js.email_accounts_center.bulk.edit_changes_notice") ||
                i18n.t("js.email_accounts_center.bulk.edit_description")}
            </span>
          </SheetHeader>

          <SheetBody className="h-100">
            {bulkEditError && (
              <Alert variant="destructive" className="mb-4">
                <AlertIcon>!</AlertIcon>
                <AlertContent>
                  <AlertDescription>{bulkEditError}</AlertDescription>
                </AlertContent>
              </Alert>
            )}

            <EmailAccountProfileTab account={bulkEditAccount} formData={bulkEditData} setFormData={setBulkEditData} />
          </SheetBody>

          <SheetFooter sticky={true}>
            <Button variant="primary" type="button" onClick={handleBulkEditSubmit} disabled={bulkEditLoading}>
              {bulkEditLoading ? i18n.t("js.loading") : i18n.t("js.save_changes")}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <InboxProtectionModal
        isOpen={isWarmupEnabled && inboxProtectionModalOpen}
        onClose={() => setInboxProtectionModalOpen(false)}
        onLearnMore={handleLearnMoreWarmup}
        emailAccountIDs={inboxProtectionAccountIds}
        stripePublicKey={stripePublicKey}
        creditCardInfo={creditCardInfo}
        cardholderName={cardholderName}
        billingInfo={billingInfo}
        userEmail={userEmail}
        pricePerWarmup={pricePerWarmup}
      />

      {/* Confirmation Dialogs */}
      {confirmBulkDisconnectOpen && (
        <Dialog ref={bulkDisconnectDialogRef}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{i18n.t("js.email_accounts_center.actions.disconnect")}</DialogTitle>
              <DialogCloseButton onClick={() => setConfirmBulkDisconnectOpen(false)} />
            </DialogHeader>
            <DialogBody>
              <p>
                {i18n.t("js.email_accounts_center.bulk.disconnect_confirm", {
                  count: selectedIds.length,
                })}
              </p>
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirmBulkDisconnectOpen(false)}>
                {i18n.t("js.cancel")}
              </Button>
              <Button
                variant="danger"
                onClick={async () => {
                  setConfirmBulkDisconnectOpen(false)
                  await executeBulkDisconnect()
                }}
              >
                {i18n.t("js.email_accounts_center.actions.disconnect")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {confirmBulkCancelRenewalOpen && (
        <Dialog ref={bulkCancelRenewalDialogRef}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{i18n.t("js.email_accounts_center.actions.cancel_renewal")}</DialogTitle>
              <DialogCloseButton onClick={() => setConfirmBulkCancelRenewalOpen(false)} />
            </DialogHeader>
            <DialogBody>
              <p>
                {i18n.t("js.email_accounts_center.bulk.cancel_renewal_confirm", {
                  count: selectedIds.length,
                })}
              </p>
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirmBulkCancelRenewalOpen(false)}>
                {i18n.t("js.cancel")}
              </Button>
              <Button
                variant="danger"
                onClick={async () => {
                  setConfirmBulkCancelRenewalOpen(false)
                  await executeBulkCancelRenewal()
                }}
              >
                {i18n.t("js.email_accounts_center.actions.cancel_renewal")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {confirmIndividualDisconnectOpen && pendingAction?.account && (
        <Dialog ref={individualDisconnectDialogRef}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{i18n.t("js.email_accounts_center.actions.disconnect")}</DialogTitle>
              <DialogCloseButton onClick={() => setConfirmIndividualDisconnectOpen(false)} />
            </DialogHeader>
            <DialogBody>
              <p>
                {i18n.t("js.email_accounts_center.bulk.disconnect_confirm", {
                  count: 1,
                })}
              </p>
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirmIndividualDisconnectOpen(false)}>
                {i18n.t("js.cancel")}
              </Button>
              <Button
                variant="danger"
                onClick={async () => {
                  setConfirmIndividualDisconnectOpen(false)
                  if (pendingAction.account) {
                    await executeIndividualDisconnect(pendingAction.account)
                  }
                }}
              >
                {i18n.t("js.email_accounts_center.actions.disconnect")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {confirmIndividualCancelRenewalOpen && pendingAction?.account && (
        <Dialog ref={individualCancelRenewalDialogRef}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{i18n.t("js.email_accounts_center.actions.cancel_renewal")}</DialogTitle>
              <DialogCloseButton onClick={() => setConfirmIndividualCancelRenewalOpen(false)} />
            </DialogHeader>
            <DialogBody>
              <p>
                {i18n.t("js.email_accounts_center.bulk.cancel_renewal_confirm", {
                  count: 1,
                })}
              </p>
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirmIndividualCancelRenewalOpen(false)}>
                {i18n.t("js.cancel")}
              </Button>
              <Button
                variant="danger"
                onClick={async () => {
                  setConfirmIndividualCancelRenewalOpen(false)
                  if (pendingAction.account) {
                    await executeIndividualCancelRenewal(pendingAction.account)
                  }
                }}
              >
                {i18n.t("js.email_accounts_center.actions.cancel_renewal")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
